<template>
  <div class="avc">
    <RadioGroup
      :model-value="mode"
      label="Quand souhaitez-vous organiser cette formation ?"
      name="dates_mode"
      :options="AVAILABILITY_MODE_OPTIONS"
      variant="card"
      :columns="AVAILABILITY_MODE_OPTIONS.length"
      @update:model-value="setMode"
    />

    <template v-if="mode === AVAILABILITY_MODES.DAYS">
      <p class="avc-intro">
        Sélectionnez tous les jours où la formation peut avoir lieu. Nous retiendrons les dates parmi celles-ci.
      </p>
      <AvailabilityCalendar :model-value="dates" @update:model-value="$emit('update:dates', $event)" />
    </template>

    <template v-else-if="mode === AVAILABILITY_MODES.PERIODS">
      <p class="avc-intro">Quelle période vous conviendrait pour organiser la formation&nbsp;?</p>
      <MonthPeriodPicker
        :model-value="periods"
        data-testid="quote-dates-periodes"
        @update:model-value="$emit('update:periods', $event)"
      />
    </template>

    <!-- Ce qu'un écran ajoute sous la saisie (la consigne de durée du formulaire
         public, son message d'erreur), avec le choix en cours. -->
    <slot :mode="mode"></slot>
  </div>
</template>

<script setup>
/**
 * « QUAND ORGANISER LA FORMATION ? » — le choix en trois cartes et sa saisie.
 *
 * UN SEUL COMPOSANT pour les deux endroits où ces disponibilités se saisissent :
 * la demande de devis du client (QuoteFormAvailability) et la fiche du
 * commercial, tunnel « Analyse du besoin » compris (QuoteDetailAvailability).
 * Mêmes cartes, mêmes mots, même exclusivité : un jour coché efface les mois, un
 * mois efface les jours, « je ne connais pas encore » efface les deux, et le
 * changement de choix vide les autres saisies (useAvailabilityMode). Une
 * évolution de l'un est donc celle de l'autre.
 *
 * Il écrit les trois colonnes de la demande (dates_disponibilites,
 * dates_periodes, dates_inconnues) par événements, sans muter ses props : le
 * parent applique. Ce que le formulaire public ajoute pour son visiteur (la
 * consigne de durée, le message d'erreur) vient par le slot, dans
 * QuoteFormAvailability.
 */
import AvailabilityCalendar from './AvailabilityCalendar.vue'
import MonthPeriodPicker from './MonthPeriodPicker.vue'
import { AVAILABILITY_MODES, AVAILABILITY_MODE_OPTIONS, useAvailabilityMode } from '../../composables/calendar/useAvailabilityMode.js'
import { RadioGroup } from '../ui'

const props = defineProps({
  /** Jours précis possibles (YYYY-MM-DD). */
  dates: { type: Array, default: () => [] },
  /** Mois possibles (YYYY-MM). */
  periods: { type: Array, default: () => [] },
  /** « Je ne connais pas encore les dates ». */
  unknown: { type: Boolean, default: false }
})

const emit = defineEmits(['update:dates', 'update:periods', 'update:unknown'])

// `useAvailabilityMode` lit et écrit trois champs sur un objet : celui-ci les
// lit dans les props et écrit en émettant, sans que le composable sache d'où
// ils viennent. Volontairement pas réactif : seul le parent qui applique ce
// qu'on émet (donc un changement de props) doit faire relire le choix.
const saisie = {
  get dates_disponibilites() { return props.dates },
  set dates_disponibilites(value) { emit('update:dates', value) },
  get dates_periodes() { return props.periods },
  set dates_periodes(value) { emit('update:periods', value) },
  get dates_inconnues() { return props.unknown },
  set dates_inconnues(value) { emit('update:unknown', value) }
}

const { mode, setMode } = useAvailabilityMode(saisie)
</script>

<style scoped>
.avc {
  display: flex;
  flex-direction: column;
  gap: var(--public-gap-field);
}

.avc-intro {
  margin: 0;
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--color-text-tertiary);
}
</style>
